import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { toast } from 'react-hot-toast';

const CityModal = ({ 
  isOpen, 
  onClose, 
  mode, 
  city, 
  onSave,
  cities = []
}) => {
  const [formData, setFormData] = useState({
    name: '',
    country: '',
    state: '',
    deliveryRadius: 25,
    status: 'active',
    coordinates: { lat: '', lng: '' }
  });
  const [areas, setAreas] = useState([]);
  const [newArea, setNewArea] = useState({ name: '', pinCodes: [] });
  const [newPinCode, setNewPinCode] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Initialize form data
  useEffect(() => {
    if (!isOpen) return;

    if (mode === 'edit' && city) {
      setFormData({
        name: city?.name || '',
        country: city?.country || '',
        state: city?.state || '',
        deliveryRadius: city?.deliveryRadius || 25,
        status: city?.status || 'active',
        coordinates: city?.coordinates || { lat: '', lng: '' }
      });
      setAreas(city?.areas || []);
    } else {
      setFormData({
        name: '',
        country: '',
        state: '',
        deliveryRadius: 25,
        status: 'active',
        coordinates: { lat: '', lng: '' }
      });
      setAreas([]);
    }
    
    setNewArea({ name: '', pinCodes: [] });
    setNewPinCode('');
    setErrors({});
  }, [isOpen, mode, city]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'City name is required';
    } else if (formData?.name?.length < 2) {
      newErrors.name = 'City name must be at least 2 characters';
    }

    if (!formData?.country?.trim()) {
      newErrors.country = 'Country is required';
    }

    if (!formData?.state?.trim()) {
      newErrors.state = 'State/Province is required';
    }

    if (formData?.deliveryRadius < 1 || formData?.deliveryRadius > 200) {
      newErrors.deliveryRadius = 'Delivery radius must be between 1 and 200 km';
    }

    if (formData?.coordinates?.lat && (isNaN(formData?.coordinates?.lat) || Math.abs(formData?.coordinates?.lat) > 90)) {
      newErrors.latitude = 'Invalid latitude value';
    }

    if (formData?.coordinates?.lng && (isNaN(formData?.coordinates?.lng) || Math.abs(formData?.coordinates?.lng) > 180)) {
      newErrors.longitude = 'Invalid longitude value';
    }

    // Check for duplicate city names in same country
    const existingCity = cities?.find(c => 
      c?.id !== city?.id && 
      c?.name?.toLowerCase()?.trim() === formData?.name?.toLowerCase()?.trim() &&
      c?.country?.toLowerCase()?.trim() === formData?.country?.toLowerCase()?.trim()
    );
    if (existingCity) {
      newErrors.name = 'City already exists in this country';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      const cityData = {
        ...formData,
        name: formData?.name?.trim(),
        country: formData?.country?.trim(),
        state: formData?.state?.trim(),
        areas,
        coordinates: {
          lat: formData?.coordinates?.lat ? parseFloat(formData?.coordinates?.lat) : null,
          lng: formData?.coordinates?.lng ? parseFloat(formData?.coordinates?.lng) : null
        }
      };

      await onSave(cityData, mode);
      onClose();
      toast?.success(`City ${mode === 'edit' ? 'updated' : 'created'} successfully`);
    } catch (error) {
      toast?.error('Failed to save city. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPinCode = () => {
    if (!newPinCode?.trim()) return;
    
    const pinCode = newPinCode?.trim()?.toUpperCase();
    if (newArea?.pinCodes?.includes(pinCode)) {
      toast?.error('Pin code already added');
      return;
    }

    setNewArea(prev => ({
      ...prev,
      pinCodes: [...prev?.pinCodes, pinCode]
    }));
    setNewPinCode('');
  };

  const handleRemovePinCode = (pinCode) => {
    setNewArea(prev => ({
      ...prev,
      pinCodes: prev?.pinCodes?.filter(p => p !== pinCode)
    }));
  };

  const handleAddArea = () => {
    if (!newArea?.name?.trim()) {
      toast?.error('Area name is required');
      return;
    }

    if (newArea?.pinCodes?.length === 0) {
      toast?.error('At least one pin code is required');
      return;
    }

    if (areas?.some(area => area?.name?.toLowerCase() === newArea?.name?.toLowerCase()?.trim())) {
      toast?.error('Area name already exists');
      return;
    }

    const area = {
      id: Date.now(),
      name: newArea?.name?.trim(),
      pinCodes: newArea?.pinCodes,
      coordinates: { lat: null, lng: null }
    };

    setAreas(prev => [...prev, area]);
    setNewArea({ name: '', pinCodes: [] });
  };

  const handleRemoveArea = (areaId) => {
    setAreas(prev => prev?.filter(area => area?.id !== areaId));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              {mode === 'edit' ? 'Edit City' : 'Add New City'}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Configure city details, delivery zones, and area coverage
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
              {/* Left Column - Basic Info */}
              <div className="space-y-6">
                <h4 className="text-md font-semibold text-foreground border-b border-border pb-2">
                  Basic Information
                </h4>

                {/* City Name */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    City Name *
                  </label>
                  <input
                    type="text"
                    value={formData?.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e?.target?.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                      errors?.name ? 'border-red-500' : 'border-border'
                    }`}
                    placeholder="Enter city name"
                    disabled={loading}
                  />
                  {errors?.name && (
                    <p className="text-red-500 text-sm mt-1">{errors?.name}</p>
                  )}
                </div>

                {/* Country & State */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Country *
                    </label>
                    <input
                      type="text"
                      value={formData?.country}
                      onChange={(e) => setFormData(prev => ({ ...prev, country: e?.target?.value }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                        errors?.country ? 'border-red-500' : 'border-border'
                      }`}
                      placeholder="Country"
                      disabled={loading}
                    />
                    {errors?.country && (
                      <p className="text-red-500 text-sm mt-1">{errors?.country}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      State/Province *
                    </label>
                    <input
                      type="text"
                      value={formData?.state}
                      onChange={(e) => setFormData(prev => ({ ...prev, state: e?.target?.value }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                        errors?.state ? 'border-red-500' : 'border-border'
                      }`}
                      placeholder="State/Province"
                      disabled={loading}
                    />
                    {errors?.state && (
                      <p className="text-red-500 text-sm mt-1">{errors?.state}</p>
                    )}
                  </div>
                </div>

                {/* Delivery Radius & Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Delivery Radius (km) *
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="200"
                      value={formData?.deliveryRadius}
                      onChange={(e) => setFormData(prev => ({ ...prev, deliveryRadius: parseInt(e?.target?.value) || 0 }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                        errors?.deliveryRadius ? 'border-red-500' : 'border-border'
                      }`}
                      disabled={loading}
                    />
                    {errors?.deliveryRadius && (
                      <p className="text-red-500 text-sm mt-1">{errors?.deliveryRadius}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Status
                    </label>
                    <select
                      value={formData?.status}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e?.target?.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      disabled={loading}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>

                {/* Coordinates */}
                <div>
                  <h5 className="text-sm font-medium text-foreground mb-2">
                    Coordinates (Optional)
                  </h5>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <input
                        type="number"
                        step="any"
                        value={formData?.coordinates?.lat}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          coordinates: { ...prev?.coordinates, lat: e?.target?.value }
                        }))}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                          errors?.latitude ? 'border-red-500' : 'border-border'
                        }`}
                        placeholder="Latitude"
                        disabled={loading}
                      />
                      {errors?.latitude && (
                        <p className="text-red-500 text-sm mt-1">{errors?.latitude}</p>
                      )}
                    </div>
                    <div>
                      <input
                        type="number"
                        step="any"
                        value={formData?.coordinates?.lng}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          coordinates: { ...prev?.coordinates, lng: e?.target?.value }
                        }))}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                          errors?.longitude ? 'border-red-500' : 'border-border'
                        }`}
                        placeholder="Longitude"
                        disabled={loading}
                      />
                      {errors?.longitude && (
                        <p className="text-red-500 text-sm mt-1">{errors?.longitude}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Areas */}
              <div className="space-y-6">
                <h4 className="text-md font-semibold text-foreground border-b border-border pb-2">
                  Areas & Pin Codes
                </h4>

                {/* Add New Area */}
                <div className="bg-muted/30 p-4 rounded-lg">
                  <h5 className="text-sm font-medium text-foreground mb-3">Add New Area</h5>
                  
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={newArea?.name}
                      onChange={(e) => setNewArea(prev => ({ ...prev, name: e?.target?.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Area name"
                      disabled={loading}
                    />
                    
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={newPinCode}
                        onChange={(e) => setNewPinCode(e?.target?.value?.toUpperCase())}
                        onKeyPress={(e) => e?.key === 'Enter' && (e?.preventDefault(), handleAddPinCode())}
                        className="flex-1 px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="Pin code"
                        disabled={loading}
                      />
                      <button
                        type="button"
                        onClick={handleAddPinCode}
                        className="px-3 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                        disabled={loading || !newPinCode?.trim()}
                      >
                        <Icon name="Plus" size={16} />
                      </button>
                    </div>

                    {newArea?.pinCodes?.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {newArea?.pinCodes?.map((pin, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-1 bg-primary/10 text-primary rounded text-sm"
                          >
                            {pin}
                            <button
                              type="button"
                              onClick={() => handleRemovePinCode(pin)}
                              className="ml-1 hover:text-primary/70"
                            >
                              <Icon name="X" size={12} />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={handleAddArea}
                      className="w-full px-3 py-2 border border-primary text-primary rounded-lg hover:bg-primary/10 transition-colors"
                      disabled={loading || !newArea?.name?.trim() || newArea?.pinCodes?.length === 0}
                    >
                      Add Area
                    </button>
                  </div>
                </div>

                {/* Existing Areas */}
                <div>
                  <h5 className="text-sm font-medium text-foreground mb-3">
                    Configured Areas ({areas?.length})
                  </h5>
                  
                  {areas?.length > 0 ? (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {areas?.map((area) => (
                        <div key={area?.id} className="border border-border rounded-lg p-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h6 className="font-medium text-foreground">{area?.name}</h6>
                              <div className="flex flex-wrap gap-1 mt-2">
                                {area?.pinCodes?.map((pin, index) => (
                                  <span
                                    key={index}
                                    className="px-2 py-0.5 bg-primary/10 text-primary rounded text-xs"
                                  >
                                    {pin}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveArea(area?.id)}
                              className="p-1 hover:bg-destructive/10 rounded transition-colors"
                              title="Remove area"
                            >
                              <Icon name="Trash2" size={16} className="text-destructive" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-sm text-muted-foreground border border-dashed border-border rounded-lg">
                      No areas configured yet. Add areas with pin codes to define coverage.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-border bg-muted/20">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-border rounded-lg hover:bg-muted transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center space-x-2"
              disabled={loading}
            >
              {loading && <Icon name="Loader2" size={16} className="animate-spin" />}
              <span>{loading ? 'Saving...' : 'Save City'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CityModal;